// Report-param grammar for a rapid-association block, ported from the coffee block, its chart view,
// its controls strip and the shared items list.
//
// Five params carry the block's state, addressed by prefix:
//   tcv  which of the three views the question is drawn as: bars, stacked bars or the scatter plot
//   cm   the chart's mode -- or the marker saying the word cloud is showing instead of a chart
//   z    the chart's zoom
//   s    the sort order
//   cb   which words are open in the stacked view, as a dot-joined list of positions
//
// `cm` is overloaded exactly as the answer-variants family overloads it, and the sort, collapse and
// cloud-marker rules are that family's -- they are imported rather than copied. What is this
// family's own is `tcv`, and the way the view chosen there decides what the chart may be drawn in.

import { isWcChartMode } from '@aytm/app/stats/page/question-views/answer-variants/lib/report-params'
import {
  PERCENT_CHART_MODES,
  calcDrawableChartMode
} from '@aytm/app/stats/page/question-views/components/lib/chart-modes'
import {
  resolveChartMode,
  resolveSortOrder
} from '@aytm/app/stats/page/question-views/answer-variants/lib/report-params'
import { decodeUncollapsedParam } from '@aytm/app/stats/page/question-views/components/lib/uncollapsed-param'
import { decodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export {
  calcSortParamsSection,
  calcZoomSection,
  DEFAULT_SORT_ORDER,
  isWcChartMode,
  resolveChartMode,
  resolveSortOrder,
  WC_CHART_MODE
} from '@aytm/app/stats/page/question-views/answer-variants/lib/report-params'
import { getChartModeTypes } from '@aytm/app/stats/page/question-views/sft/components/chart/lib/chartModes'

export const DEFAULT_VIEW_MODE = 'bar'

export const VIEW_MODES = ['bar', 'stacked', 'scatter']

const SCATTER_CHART_MODE = 'scatter'

// A view the question cannot be drawn as is read as no view at all.
export const resolveViewMode = ({ tcv } = {}) => (VIEW_MODES.includes(tcv) ? tcv : DEFAULT_VIEW_MODE)

export const calcViewModeSection = viewMode => ({
  tcv: viewMode === DEFAULT_VIEW_MODE ? null : String(viewMode)
})

// Only the bar view has an axis to zoom, so leaving it drops the zoom with it.
export const hasZoom = viewMode => DEFAULT_VIEW_MODE === viewMode

// What a view does not draw it keeps no state for: the stacked view replaces the items list with
// its words' sub-batches, so it has no sort, and only it has words to open.
export const hasItemsList = viewMode => 'stacked' !== viewMode

export const hasSubBatches = viewMode => 'stacked' === viewMode

// A sort survives a view switch only where the new view draws the column it names: the reaction
// time is a column of the scatter list alone.
export const keepsSortOrder = (viewMode, sortOrder) =>
  hasItemsList(viewMode) && ('scatter' === viewMode || !String(sortOrder ?? '').startsWith('time-'))

// The scatter plot is its own mode and admits no other; the stacked view stacks shares, so it can
// only be drawn in percents. A question counting respondents has no pie to slice.
const calcPreferredChartMode = ({ chartMode, questionChartMode, globalChartMode, viewMode, percentsOnly } = {}) => {
  let mode = 'off' === chartMode ? globalChartMode : chartMode
  mode = mode || questionChartMode || globalChartMode

  if ('pie' === mode) mode = 'respondents'

  if (SCATTER_CHART_MODE === viewMode) return SCATTER_CHART_MODE

  if ('stacked' === viewMode || true === percentsOnly) {
    return PERCENT_CHART_MODES.includes(mode) ? mode : 'percents'
  }

  return mode
}

// whatever the rules above settle on is fitted to the modes the view offers
export const calcDefaultChartMode = (args = {}) =>
  calcDrawableChartMode(calcPreferredChartMode(args), getChartModeTypes(args.viewMode, args.percentsOnly), {
    globalChartMode: args.globalChartMode
  })

// A chart mode that says what the question already declares is not worth storing; any other pick is
// stored as it is. There is no word cloud here, so a declared cloud is just a mode the question does
// not offer, not a branch a pick has to leave. Picking a view the chart mode cannot survive drops it,
// and the zoom with it.
export const calcChartParamsSection = ({ chartMode, questionChartMode, viewMode } = {}) => {
  const section = { cm: chartMode === (questionChartMode || 'off') ? null : chartMode || null }

  return hasZoom(viewMode) ? section : { ...section, z: null }
}

// The state a block opens in, read off the params bag it is handed: every key by its own rule.
export const resolveReportParams = ({ params = {}, defaultSortOrder } = {}) => ({
  viewMode: resolveViewMode(params),
  // the reader's own pick only: the mode the question declares arrives on its own
  chartMode: params.cm || null,
  zoomLevel: decodeZoomParam(params.z),
  sortOrder: resolveSortOrder(params.s, defaultSortOrder),
  uncollapsed: decodeUncollapsedParam(params.cb)
})
